import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface EditModuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onModuleUpdated: () => void;
  moduleId: number;
}

interface ModuleFormData {
  title: string;
  description: string;
  order: number;
  is_completed: boolean;
}

const EditModuleForm: React.FC<EditModuleFormProps> = ({ isOpen, onClose, onModuleUpdated, moduleId }) => {
  const [formData, setFormData] = useState<ModuleFormData>({
    title: '',
    description: '',
    order: 1,
    is_completed: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModuleData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/modules/${moduleId}/`);
      const moduleData = response.data;
      setFormData({
        title: moduleData.title,
        description: moduleData.description || '',
        order: moduleData.order,
        is_completed: moduleData.is_completed,
      });
    } catch (error) {
      console.error('Error fetching module:', error);
      setError('Failed to load module data');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (isOpen && moduleId) {
      fetchModuleData();
    }
  }, [isOpen, moduleId, fetchModuleData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await apiClient.patch(`/api/modules/${moduleId}/`, formData);
      onModuleUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating module:', error);
      setError('Failed to update module');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(107, 114, 128, 0.75)',
            transition: 'opacity 0.2s ease-in-out',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel style={{
                position: 'relative',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                maxWidth: '32rem',
              }}>
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  paddingRight: SPACING[16],
                  paddingTop: SPACING[16],
                }}>
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                    }}
                    onClick={onClose}
                  >
                    <span style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      borderWidth: 0,
                    }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                }}>
                  <div style={{
                    marginTop: 0,
                    textAlign: 'left',
                    width: '100%',
                  }}>
                    <Dialog.Title as="h3" style={{
                      fontSize: TYPOGRAPHY.headings.sizes.h2,
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      lineHeight: '1.5',
                      color: COLORS.text.primary,
                    }}>
                      Edit Module
                    </Dialog.Title>

                    {error && (
                      <div style={{
                        marginTop: SPACING[8],
                        borderRadius: RADIUS,
                        backgroundColor: '#FEF2F2',
                        padding: SPACING[16],
                      }}>
                        <div style={{
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.status.error,
                        }}>{error}</div>
                      </div>
                    )}

                    {loading ? (
                      <div style={{
                        marginTop: SPACING[16],
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: `2px solid ${COLORS.border.default}`,
                          borderBottomColor: COLORS.accent.default,
                          animation: 'spin 1s linear infinite',
                        }}></div>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} style={{
                        marginTop: SPACING[16],
                        display: 'flex',
                        flexDirection: 'column',
                        gap: SPACING[16],
                      }}>
                        <div>
                          <label htmlFor="title" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            marginBottom: SPACING[4],
                          }}>
                            Title
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="description" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            marginBottom: SPACING[4],
                          }}>
                            Description
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="order" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                            marginBottom: SPACING[4],
                          }}>
                            Order
                          </label>
                          <input
                            type="number"
                            id="order"
                            value={formData.order}
                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            min="1"
                            required
                          />
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <input
                            type="checkbox"
                            id="is_completed"
                            checked={formData.is_completed}
                            onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
                            style={{
                              height: '16px',
                              width: '16px',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              accentColor: COLORS.accent.default,
                              cursor: 'pointer',
                            }}
                          />
                          <label htmlFor="is_completed" style={{
                            marginLeft: SPACING[8],
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            color: COLORS.text.secondary,
                          }}>
                            Mark as completed
                          </label>
                        </div>

                        <div style={{
                          marginTop: SPACING[24],
                          display: 'flex',
                          flexDirection: 'row-reverse',
                          gap: SPACING[12],
                        }}>
                          <button
                            type="submit"
                            disabled={loading}
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              backgroundColor: loading ? COLORS.text.tertiary : COLORS.accent.default,
                              padding: `${SPACING[8]} ${SPACING[16]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: COLORS.bg.panel,
                              border: 'none',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.5 : 1,
                            }}
                          >
                            Save Changes
                          </button>
                          <button
                            type="button"
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              backgroundColor: COLORS.bg.panel,
                              padding: `${SPACING[8]} ${SPACING[16]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: COLORS.text.primary,
                              border: `1px solid ${COLORS.border.default}`,
                              cursor: 'pointer',
                            }}
                            onClick={onClose}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditModuleForm;
